import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import ActivityFeed from '../ActivityFeed/ActivityFeed';

import css from './TransactionPanel.css';

// Functional component as a helper to build ActivityFeed section
const FeedSection = props => {
  const {
    className,
    rootClassName,
    currentTransaction,
    currentUser,
    ownReview,
    fetchMessagesError,
    fetchMessagesInProgress,
    initialMessageFailed,
    messages,
    oldestMessagePageFetched,
    onShowMoreMessages,
    onOpenReviewModal,
    onOpenOfferModal,
    onOpenInvoiceModal,
    vatPreview = null,
    totalMessagePages,
    intl,
    stepActions,
    actionInProgress,
    onManageDisableScrolling,
    onDownloadAttachment,
    onOpenAttachment,
    onFetchAttachmentPreview,
    attachmentPreviewUrls,
    downloadAttachmentInProgressIds,
    downloadAttachmentError,
  } = props;

  const feedContainerRef = useRef(null);
  const previousMessagesLengthRef = useRef(messages.length);
  const previousTransitionRef = useRef(currentTransaction?.attributes?.lastTransition);
  const wasAtBottomRef = useRef(true); // Track if user is at bottom

  const hasOlderMessages = totalMessagePages > oldestMessagePageFetched;

  const showFeed = messages.length > 0 || initialMessageFailed || fetchMessagesError;

  const classes = classNames(rootClassName || css.feedContainer, className);

  // Helper function to check if scrolled to bottom
  const isScrolledToBottom = (element, threshold = 50) => {
    if (!element) return false;
    return element.scrollHeight - element.scrollTop - element.clientHeight < threshold;
  };

  // Helper function to scroll to bottom
  const scrollToBottom = () => {
    if (feedContainerRef.current) {
      const conversationContent = feedContainerRef.current.parentElement;
      const conversationWrapper = conversationContent?.parentElement;
      if (conversationWrapper) {
        conversationWrapper.scrollTop = conversationWrapper.scrollHeight;
      }
    }
  };

  // Auto-scroll to bottom when new messages/transitions are added
  useEffect(() => {
    const currentMessagesLength = messages.length;
    const currentTransition = currentTransaction?.attributes?.lastTransition;
    const hasNewMessage = currentMessagesLength > previousMessagesLengthRef.current;
    const hasNewTransition =
      currentTransition !== previousTransitionRef.current && currentTransition;

    if (hasNewMessage || hasNewTransition) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }

    // Update refs
    previousMessagesLengthRef.current = currentMessagesLength;
    previousTransitionRef.current = currentTransition;
  }, [messages.length, currentTransaction?.attributes?.lastTransition]);

  // Track scroll position to maintain "at bottom" state
  useEffect(() => {
    if (!feedContainerRef.current) return;

    const conversationContent = feedContainerRef.current.parentElement;
    if (!conversationContent) return;

    const conversationWrapper = conversationContent.parentElement;
    if (!conversationWrapper) return;

    const handleScroll = () => {
      wasAtBottomRef.current = isScrolledToBottom(conversationWrapper, 50);
    };

    conversationWrapper.addEventListener('scroll', handleScroll);
    return () => {
      conversationWrapper.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Watch for textarea height changes and adjust scroll position
  useEffect(() => {
    if (!feedContainerRef.current) return;

    // FeedSection -> conversationContent -> conversationWrapper (scrollable)
    const conversationContent = feedContainerRef.current.parentElement;
    if (!conversationContent) return;

    const conversationWrapper = conversationContent.parentElement;
    if (!conversationWrapper) return;

    // conversationAndMessageFormWrapper is the parent that includes both feed and form
    const conversationAndMessageFormWrapper = conversationWrapper.parentElement;
    if (!conversationAndMessageFormWrapper) return;

    // Find the SendMessageForm within the wrapper (it's a sibling of conversationWrapper)
    const sendMessageForm = conversationAndMessageFormWrapper.querySelector('form');
    if (!sendMessageForm) return;

    let previousFormHeight = sendMessageForm.offsetHeight;

    // Observe size changes in the message form (when textarea grows)
    const resizeObserver = new ResizeObserver(entries => {
      const currentFormHeight = sendMessageForm.offsetHeight;
      const heightDelta = currentFormHeight - previousFormHeight;

      if (heightDelta !== 0) {
        // Adjust scroll position by the amount the form grew/shrunk
        // This maintains the visual position of the content
        requestAnimationFrame(() => {
          conversationWrapper.scrollTop += heightDelta;
        });

        previousFormHeight = currentFormHeight;
      }
    });

    // Observe the message form directly (this will fire when textarea height changes)
    resizeObserver.observe(sendMessageForm);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return showFeed ? (
    <div className={classes} ref={feedContainerRef}>
      {initialMessageFailed ? (
        <p className={css.messageError}>
          <FormattedMessage id="TransactionPanel.initialMessageFailed" />
        </p>
      ) : null}
      {fetchMessagesError ? (
        <p className={css.messageError}>
          <FormattedMessage id="TransactionPanel.messageLoadingFailed" />
        </p>
      ) : null}
      {downloadAttachmentError ? (
        <p className={css.messageError}>
          <FormattedMessage id="TransactionPanel.attachmentDownloadFailed" />
        </p>
      ) : null}
      <ActivityFeed
        transaction={currentTransaction}
        messages={messages}
        currentUser={currentUser}
        ownReview={ownReview}
        hasOlderMessages={hasOlderMessages && !fetchMessagesInProgress}
        onShowOlderMessages={onShowMoreMessages}
        onOpenReviewModal={onOpenReviewModal}
        onOpenOfferModal={onOpenOfferModal}
        onOpenInvoiceModal={onOpenInvoiceModal}
        vatPreview={vatPreview}
        fetchMessagesInProgress={fetchMessagesInProgress}
        stepActions={stepActions}
        actionInProgress={actionInProgress}
        onManageDisableScrolling={onManageDisableScrolling}
        onDownloadAttachment={onDownloadAttachment}
        onOpenAttachment={onOpenAttachment}
        onFetchAttachmentPreview={onFetchAttachmentPreview}
        attachmentPreviewUrls={attachmentPreviewUrls}
        downloadAttachmentInProgressIds={downloadAttachmentInProgressIds}
      />
    </div>
  ) : null;
};

export default FeedSection;
